/** Phase F wizard steps — canonical ISOCNTRL fields (never USIO field names). */
import React from 'react';
import { inputCls } from '../finance/financeUi';
import { INTENT_LABELS, getPath, tid } from './paymentAccount';

const BUSINESS = [
  ['business.legal_name', 'Legal business name'], ['business.dba_name', 'DBA (doing business as)'],
  ['business.ownership_type', 'Ownership type', 'ownership'], ['business.tax_id', 'Federal tax ID (EIN or SSN, 9 digits)', 'secret'],
  ['business.description', 'What does the business do?'], ['business.start_date', 'Business start date (MM/DD/YYYY)'],
  ['business.email', 'Business email'], ['business.phone', 'Business phone (10 digits)'],
  ['business.website', 'Website (optional)', 'optional'], ['business.address.line1', 'Street address'],
  ['business.address.city', 'City'], ['business.address.state', 'State', 'state'], ['business.address.postal_code', 'ZIP code'],
];
const OWNER = [
  ['principal.first_name', 'First name'], ['principal.last_name', 'Last name'], ['principal.title', 'Title'],
  ['principal.ownership_percent', 'Ownership %'], ['principal.dob', 'Date of birth (MM/DD/YYYY)', 'secret'],
  ['principal.ssn_last4', 'Last 4 of SSN', 'secret'], ['principal.phone', 'Mobile (10 digits)'],
  ['principal.address.line1', 'Home street address'], ['principal.address.city', 'City'],
  ['principal.address.state', 'State', 'state'], ['principal.address.postal_code', 'ZIP code'],
];
export const STEP_FIELDS = { business: BUSINESS, owner: OWNER };

function Field({ def, app, onSet, meta, onFile }) {
  const [path, label, kind] = def;
  const value = getPath(app, path) ?? '';
  const common = { className: inputCls, 'data-testid': tid(path), value, onChange: (e) => onSet(path, e.target.value) };
  const fromProfile = meta.sources?.[path];
  let input;
  if (kind === 'ownership' || kind === 'state') {
    const opts = kind === 'ownership' ? Object.entries(meta.ownership_types || {}) : (meta.states || []).map((st) => [st, st]);
    input = <select {...common}><option value="">{label}</option>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>;
  } else if (kind === 'secret') {
    input = <input {...common} type="password" autoComplete="off" placeholder={onFile ? `${label} — on file, leave blank to keep` : label} />;
  } else {
    input = <input {...common} placeholder={label} />;
  }
  return (
    <label className="block space-y-1 text-xs text-muted-foreground">
      <span className="flex items-center gap-1.5">{label}{fromProfile && <span className="rounded bg-muted px-1.5 text-[10px] uppercase tracking-wide">from your {fromProfile.replace('_', ' ')}</span>}</span>
      {input}
    </label>
  );
}

export function FieldsStep({ fields, app, onSet, meta, onFile }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="pa-fields-step">
      {fields.map((def) => <Field key={def[0]} def={def} app={app} onSet={onSet} meta={meta} onFile={onFile[def[0]]} />)}
    </div>
  );
}

export function BankStep({ bank, setBank, options }) {
  const verified = (options || []).filter((o) => o.verification_status === 'VERIFIED');
  return (
    <div className="space-y-3 text-sm" data-testid="pa-bank-step">
      {verified.map((o) => (
        <label key={o.payment_profile_id} className="flex items-start gap-2 rounded-lg border p-3">
          <input type="radio" checked={bank.source === 'PAYOUT_PROFILE' && bank.payment_profile_id === o.payment_profile_id} onChange={() => setBank({ source: 'PAYOUT_PROFILE', payment_profile_id: o.payment_profile_id })} data-testid={`pa-bank-reuse-${o.payment_profile_id}`} />
          <span>Use my verified bank ending <b>{o.last4}</b><span className="block text-xs text-muted-foreground">Same bank, new authorization: your residual payout (credit) authorization is not reused for this Payment Account.</span></span>
        </label>
      ))}
      <label className="flex items-center gap-2 rounded-lg border p-3"><input type="radio" checked={bank.source === 'NEW'} onChange={() => setBank({ source: 'NEW' })} data-testid="pa-bank-new" />Use a different bank account</label>
      {bank.source === 'NEW' && (
        <div className="grid gap-2 sm:grid-cols-2">
          <input className={inputCls} placeholder="Routing number (9 digits)" value={bank.routing_number || ''} onChange={(e) => setBank({ ...bank, routing_number: e.target.value })} data-testid="pa-bank-routing" />
          <input className={inputCls} type="password" autoComplete="off" placeholder="Account number" value={bank.account_number || ''} onChange={(e) => setBank({ ...bank, account_number: e.target.value })} data-testid="pa-bank-account" />
        </div>
      )}
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!bank.is_personal} onChange={(e) => setBank({ ...bank, is_personal: e.target.checked })} data-testid="pa-bank-personal" />This is a personal (not business) account</label>
    </div>
  );
}

export function IntentStep({ intents, setIntents }) {
  const toggle = (k) => setIntents(intents.includes(k) ? intents.filter((i) => i !== k) : [...intents, k]);
  return (
    <div className="space-y-2 text-sm" data-testid="pa-intent-step">
      <p className="text-muted-foreground">What do you plan to use this account for? This is a request — the payment provider decides what is enabled.</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {Object.entries(INTENT_LABELS).map(([k, label]) => (
          <label key={k} className="flex items-center gap-2 rounded-lg border p-3"><input type="checkbox" checked={intents.includes(k)} onChange={() => toggle(k)} data-testid={`pa-intent-${k}`} />{label}</label>
        ))}
      </div>
    </div>
  );
}

export function AuthorizationStep({ texts, accepted, onAccept }) {
  return (
    <div className="space-y-3 text-sm" data-testid="pa-auth-step">
      {Object.entries(texts || {}).map(([type, text]) => (
        <div key={type} className="rounded-lg border p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{type.replace(/_/g, ' ')}</p>
          <p className="mt-1">{text}</p>
          {accepted[type] ? <p className="mt-2 text-emerald-700" data-testid={`pa-auth-done-${type}`}>Authorized · bank ••{accepted[type].bank_last4}</p>
            : <button type="button" className="mt-2 inline-flex h-8 items-center rounded-full border px-3 text-xs hover:bg-muted" onClick={() => onAccept(type)} data-testid={`pa-auth-accept-${type}`}>I authorize</button>}
        </div>
      ))}
    </div>
  );
}
